import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { PlusIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

// Common pottery-related keywords for suggestions
const DEFAULT_SUGGESTED_KEYWORDS = [
  // Product types
  'vase', 'schale', 'tasse', 'krug', 'teller', 'becher', 'dose', 'kerzenhalter',
  // Characteristics
  'handgefertigt', 'handgemacht', 'unikat', 'einzelstück', 'künstlerisch',
  // Materials & Techniques
  'keramik', 'steinzeug', 'steingut', 'porzellan', 'ton', 'glasiert', 'unglasiert', 'raku', 'gedreht',
  // Styles
  'modern', 'minimalistisch', 'rustikal', 'organisch', 'japanisch', 'skandinavisch', 'vintage',
  // Colors
  'weiß', 'schwarz', 'blau', 'grün', 'braun', 'terrakotta', 'naturtöne', 'bunt',
  // Use cases
  'dekoration', 'geschenk', 'alltagsgebrauch', 'spülmaschinenfest', 'lebensmittelecht',
  // Sustainability
  'nachhaltig', 'handwerk', 'kunsthandwerk', 'lokal', 'regional'
];

interface KeywordSelectorProps {
  /** Currently selected keywords */
  value: string[];
  /** Callback when keywords change */
  onChange: (keywords: string[]) => void;
  /** Optional list of existing keywords from all products for suggestions */
  existingKeywords?: string[];
  /** Optional placeholder for the new keyword input */
  placeholder?: string;
  /** Optional className for the container */
  className?: string;
  /** Optional label for the field */
  label?: string;
  /** Whether the field shows a changed indicator */
  isChanged?: boolean;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Maximum number of keywords allowed */
  maxKeywords?: number;
}

/**
 * KeywordSelector - A component for selecting and managing product keywords/tags
 * 
 * Features:
 * - Checkbox selection from existing keywords
 * - Add new keywords via text input
 * - Suggestions from common pottery keywords
 * - Remove individual keywords
 * - Collapsible suggestion list
 */
const KeywordSelector: React.FC<KeywordSelectorProps> = ({
  value = [],
  onChange,
  existingKeywords = [],
  placeholder = 'Neues Keyword hinzufügen...',
  className = '',
  label,
  isChanged = false,
  disabled = false,
  maxKeywords = 20
}) => {
  const [newKeyword, setNewKeyword] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filterText, setFilterText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Combine existing keywords with default suggestions, sorted alphabetically
  const allSuggestions = useMemo(() => {
    const combined = new Set<string>([
      ...existingKeywords.map(k => k.toLowerCase().trim()),
      ...DEFAULT_SUGGESTED_KEYWORDS
    ]);
    return Array.from(combined).sort((a, b) => a.localeCompare(b, 'de'));
  }, [existingKeywords]);

  // Filter suggestions based on filter text and exclude already selected
  const filteredSuggestions = useMemo(() => {
    const filter = filterText.toLowerCase().trim();
    return allSuggestions.filter(keyword => {
      const isSelected = value.some(v => v.toLowerCase() === keyword.toLowerCase());
      const matchesFilter = !filter || keyword.toLowerCase().includes(filter);
      return !isSelected && matchesFilter;
    });
  }, [allSuggestions, value, filterText]);

  // Handle adding a new keyword
  const handleAddKeyword = useCallback(() => {
    const trimmed = newKeyword.trim().toLowerCase();
    if (!trimmed) return;
    
    // Check if already exists
    if (value.some(v => v.toLowerCase() === trimmed)) {
      setNewKeyword('');
      setFilterText('');
      return;
    }

    // Check max limit
    if (value.length >= maxKeywords) {
      return;
    }

    onChange([...value, trimmed]);
    setNewKeyword('');
    setFilterText('');
  }, [newKeyword, value, onChange, maxKeywords]);

  // Handle toggling a suggestion
  const handleToggleSuggestion = useCallback((keyword: string) => {
    const normalizedKeyword = keyword.toLowerCase();
    const isSelected = value.some(v => v.toLowerCase() === normalizedKeyword);
    
    if (isSelected) {
      onChange(value.filter(v => v.toLowerCase() !== normalizedKeyword));
    } else if (value.length < maxKeywords) {
      onChange([...value, normalizedKeyword]);
      // Reset filter when adding a keyword from suggestions
      setNewKeyword('');
      setFilterText('');
    }
  }, [value, onChange, maxKeywords]);

  // Handle removing a keyword
  const handleRemoveKeyword = useCallback((keyword: string) => {
    onChange(value.filter(v => v.toLowerCase() !== keyword.toLowerCase()));
  }, [value, onChange]);

  // Handle key press in input
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  }, [handleAddKeyword]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`space-y-3 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {isChanged && (
            <span className="ml-2 text-xs text-amber-600">(geändert)</span>
          )}
        </label>
      )}

      {/* Selected Keywords Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((keyword, index) => (
            <span
              key={`${keyword}-${index}`}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-clay-100 text-clay-800 group"
            >
              {keyword}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(keyword)}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-clay-600 hover:text-clay-900 hover:bg-clay-200 focus:outline-none"
                  aria-label={`${keyword} entfernen`}
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Add New Keyword Input */}
      {!disabled && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={newKeyword}
              onChange={(e) => {
                setNewKeyword(e.target.value);
                setFilterText(e.target.value);
              }}
              onKeyPress={handleKeyPress}
              onFocus={() => setShowSuggestions(true)}
              placeholder={placeholder}
              disabled={value.length >= maxKeywords}
              className="w-full border border-warmGray-300 rounded-md shadow-sm py-2 pl-3 pr-10 text-sm focus:ring-clay-600 focus:border-clay-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              disabled={!newKeyword.trim() || value.length >= maxKeywords}
              className="absolute inset-y-0 right-0 flex items-center px-2 text-clay-600 hover:text-clay-800 disabled:text-gray-300 disabled:cursor-not-allowed"
              aria-label="Keyword hinzufügen"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Keyword count indicator */}
      {!disabled && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{value.length} / {maxKeywords} Keywords</span>
          <button
            type="button"
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="inline-flex items-center text-clay-600 hover:text-clay-800"
          >
            {showSuggestions ? (
              <>
                <ChevronUpIcon className="w-4 h-4 mr-1" />
                Vorschläge ausblenden
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4 mr-1" />
                Vorschläge anzeigen ({filteredSuggestions.length})
              </>
            )}
          </button>
        </div>
      )}

      {/* Suggestions Panel */}
      {showSuggestions && !disabled && filteredSuggestions.length > 0 && (
        <div className="border border-warmGray-200 rounded-md bg-white shadow-sm max-h-48 overflow-y-auto">
          <div className="p-2">
            <p className="text-xs text-gray-500 mb-2 px-1">
              Klicken Sie auf ein Keyword, um es hinzuzufügen:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {filteredSuggestions.slice(0, 50).map((keyword) => {
                const isFromExisting = existingKeywords.some(
                  k => k.toLowerCase() === keyword.toLowerCase()
                );
                return (
                  <button
                    key={keyword}
                    type="button"
                    onClick={() => handleToggleSuggestion(keyword)}
                    disabled={value.length >= maxKeywords}
                    className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors
                      ${isFromExisting 
                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200' 
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                    title={isFromExisting ? 'Aus bestehenden Produkten' : 'Vorgeschlagenes Keyword'}
                  >
                    <PlusIcon className="w-3 h-3 mr-1" />
                    {keyword}
                  </button>
                );
              })}
            </div>
            {filteredSuggestions.length > 50 && (
              <p className="text-xs text-gray-400 mt-2 px-1">
                + {filteredSuggestions.length - 50} weitere Vorschläge. Tippen Sie, um zu filtern.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Empty state when no suggestions match filter */}
      {showSuggestions && !disabled && filteredSuggestions.length === 0 && filterText && (
        <div className="border border-warmGray-200 rounded-md bg-gray-50 p-3">
          <p className="text-sm text-gray-500">
            Keine passenden Vorschläge für &quot;{filterText}&quot;.
            {!value.some(v => v.toLowerCase() === filterText.toLowerCase()) && (
              <button
                type="button"
                onClick={handleAddKeyword}
                className="ml-1 text-clay-600 hover:text-clay-800 underline"
              >
                Als neues Keyword hinzufügen
              </button>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default KeywordSelector;
